import {
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  styled,
} from "@mui/material";
import { Field, useFormikContext } from "formik";
import { Select } from "formik-mui";
import { useSelectedLayer } from "hooks/selectionHooks";
import { useEffect, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useIndicator } from "../../../hooks/indicatorHooks";

import { LayerSearchFormValues, LayerProperties } from "../../../types";

interface Props {
  config: string;
  layerProperties: LayerProperties[];
  className?: string;
}

export function LayerSearchAttributeSelect(props: Props) {
  const { config, className, layerProperties } = props;
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<LayerSearchFormValues>();
  const { data: indicator } = useIndicator(
    config,
    values.layer,
    layerProperties
  );

  const { data: selectedLayer } = useSelectedLayer(
    props.config,
    indicator,
    layerProperties
  );

  const searchableAttributes = useMemo(() => {
    if (!selectedLayer) return [];
    else
      return selectedLayer.attributes.filter((attribute) => attribute.search);
  }, [selectedLayer]);

  useEffect(() => {
    const updatedAttributes = values.attributes.filter((attr) =>
      searchableAttributes.find((searchAttr) => searchAttr.key === attr)
    );
    if (updatedAttributes.length !== values.attributes.length) {
      setFieldValue("attributes", updatedAttributes);
    }
  }, [searchableAttributes, values.attributes, setFieldValue]);

  return render();

  function render() {
    if (!searchableAttributes) return null;

    return (
      <Styles className={className}>
        {!values.layer && (
          <FormControl
            fullWidth
            className="placeholder-label"
            variant="standard"
          >
            <InputLabel>{t("label.pleaseSelectALayer")}</InputLabel>
          </FormControl>
        )}
        {indicator && values.layer && (
          <Field
            component={Select}
            id="attributes"
            name="attributes"
            multiple
            variant="standard"
            formControl={{ fullWidth: true, variant: "standard" }}
            label={t("label.attributesToSearchWithin")}
          >
            {selectedLayer &&
              searchableAttributes.map((attribute) => (
                <MenuItem key={attribute.key} value={attribute.key}>
                  <Checkbox
                    checked={values.attributes.indexOf(attribute.key) > -1}
                  />
                  <ListItemText primary={attribute.label} />
                </MenuItem>
              ))}
          </Field>
        )}
      </Styles>
    );
  }
}

const Styles = styled("div")`
  .MuiSelect-multiple {
    color: ${({ theme }) => theme.palette.common.white};

    .MuiCheckbox-root {
      display: none;
    }
    .MuiListItemText-root {
      display: inline-block;
    }
  }
`;
